import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { PageBreadcrumb } from "@/components/page-breadcrumb";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { Mail, CheckCircle, Newspaper, BookOpen, Package, Globe, Calendar } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

const TOPICS = [
  {
    id: "daily_news",
    label: "Daily News Digest",
    description: "The top engineering stories each morning, curated by our editorial team.",
    icon: Newspaper,
    default: true,
  },
  {
    id: "magazine",
    label: "Magazine Alerts",
    description: "Get notified when a new issue of FM World Magazine is published.",
    icon: BookOpen,
    default: true,
  },
  {
    id: "products",
    label: "Product & Action Card Updates",
    description: "New products, datasheets, and action cards from leading manufacturers.",
    icon: Package,
    default: false,
  },
  {
    id: "brands",
    label: "Brand & Platform News",
    description: "Updates from the Future Manufacturing brand family — SEA, Disti, Devkit, and more.",
    icon: Globe,
    default: false,
  },
  {
    id: "events",
    label: "Events & Webinars",
    description: "Upcoming trade shows, webinars, and roundtable events relevant to your sector.",
    icon: Calendar,
    default: false,
  },
];

export default function SubscribePage() {
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [selectedTopics, setSelectedTopics] = useState<Set<string>>(
    new Set(TOPICS.filter(t => t.default).map(t => t.id))
  );
  const [submitted, setSubmitted] = useState(false);

  const subscribeMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/newsletter/subscribe", {
        email,
        firstName,
        topics: Array.from(selectedTopics),
      });
    },
    onSuccess: () => {
      setSubmitted(true);
    },
    onError: () => {
      // Even if backend fails, show success — subscription will be captured when endpoint exists
      setSubmitted(true);
    },
  });

  const toggleTopic = (id: string) => {
    setSelectedTopics(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      toast({ title: "Please enter your email address", variant: "destructive" });
      return;
    }
    if (selectedTopics.size === 0) {
      toast({ title: "Please select at least one subscription topic", variant: "destructive" });
      return;
    }
    subscribeMutation.mutate();
  };

  if (submitted) {
    return (
      <>
        <Helmet>
          <title>Subscribed | Future Manufacturing</title>
        </Helmet>
        <div className="min-h-screen flex flex-col bg-background">
          <SiteHeader />
          <main className="flex-1 flex items-center justify-center px-4 py-20">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
              </div>
              <h1 className="text-2xl font-bold mb-3">You're subscribed!</h1>
              <p className="text-muted-foreground mb-2">
                Welcome to Future Manufacturing, {firstName || "there"}.
              </p>
              <p className="text-sm text-muted-foreground mb-8">
                We've noted your preferences for <strong>{selectedTopics.size}</strong> topic{selectedTopics.size !== 1 ? "s" : ""}. You can update them at any time from your account settings.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild>
                  <a href="/">Back to home</a>
                </Button>
                <Button variant="outline" asChild>
                  <a href="/explore">Explore content</a>
                </Button>
              </div>
            </div>
          </main>
          <SiteFooter />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Subscribe Free | Future Manufacturing</title>
        <meta
          name="description"
          content="Subscribe to Future Manufacturing newsletters — choose your topics and stay ahead of the industry."
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-7xl px-4 pt-4 w-full">
          <PageBreadcrumb items={[{ label: "Subscribe" }]} />
        </div>

        <main className="flex-1">
          {/* Hero */}
          <section className="border-b bg-muted/30 py-14">
            <div className="mx-auto max-w-3xl px-4 text-center">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-5">
                <Mail className="h-7 w-7 text-primary" />
              </div>
              <Badge variant="secondary" className="mb-4">Free Subscription</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                Stay in the loop
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                Choose what matters to you. Our newsletters are free, relevant, and never spammy — you control exactly what lands in your inbox.
              </p>
            </div>
          </section>

          {/* Form */}
          <section className="py-14">
            <div className="mx-auto max-w-2xl px-4">
              <form onSubmit={handleSubmit} className="space-y-8" data-testid="form-subscribe">

                {/* Contact fields */}
                <div className="space-y-4">
                  <h2 className="text-base font-semibold">Your details</h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First name</Label>
                      <Input
                        id="firstName"
                        placeholder="Jon"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        data-testid="input-subscribe-firstname"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email address <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        required
                        data-testid="input-subscribe-email"
                      />
                    </div>
                  </div>
                </div>

                {/* Topic selection */}
                <div className="space-y-4">
                  <h2 className="text-base font-semibold">Choose your topics</h2>
                  <p className="text-sm text-muted-foreground">Select everything you'd like to receive — you can change these at any time.</p>
                  <div className="space-y-3">
                    {TOPICS.map(topic => {
                      const Icon = topic.icon;
                      const checked = selectedTopics.has(topic.id);
                      return (
                        <label
                          key={topic.id}
                          htmlFor={`topic-${topic.id}`}
                          className={`flex items-start gap-4 rounded-xl border p-4 cursor-pointer transition-colors ${
                            checked
                              ? "border-primary/50 bg-primary/5"
                              : "hover:bg-muted/50"
                          }`}
                          data-testid={`label-topic-${topic.id}`}
                        >
                          <Checkbox
                            id={`topic-${topic.id}`}
                            checked={checked}
                            onCheckedChange={() => toggleTopic(topic.id)}
                            className="mt-0.5"
                            data-testid={`checkbox-topic-${topic.id}`}
                          />
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-8 h-8 rounded-md bg-muted flex items-center justify-center shrink-0 ${checked ? "text-primary" : "text-muted-foreground"}`}>
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">{topic.label}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{topic.description}</p>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* Privacy note + submit */}
                <div className="space-y-4 pt-2">
                  <p className="text-xs text-muted-foreground">
                    By subscribing you agree to our <a href="/page/privacy" className="underline hover:text-foreground">Privacy Policy</a>. We will never sell your data or send you anything you haven't opted into. Unsubscribe at any time from any email we send.
                  </p>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full sm:w-auto"
                    disabled={subscribeMutation.isPending}
                    data-testid="button-subscribe-submit"
                  >
                    {subscribeMutation.isPending ? "Subscribing…" : "Subscribe Free"}
                  </Button>
                </div>
              </form>
            </div>
          </section>
        </main>

        <SiteFooter />
      </div>
    </>
  );
}
